import { CaretLeft, CaretRight } from "@phosphor-icons/react";

import { Button } from "common/components/ui/Button";
import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import type { LinkedInProfile } from "common/types";

import type { Dispatch, SetStateAction } from "react";

interface PreviewHeaderProps {
  targetProfiles: LinkedInProfile[];
  currentTargetIndex: number;
  setCurrentTargetIndex: Dispatch<SetStateAction<number>>;
}

export default function PreviewHeader({
  targetProfiles,
  currentTargetIndex,
  setCurrentTargetIndex,
}: PreviewHeaderProps) {
  const isLoading = !targetProfiles;
  const selectedProfile = targetProfiles
    ? targetProfiles[currentTargetIndex]
    : undefined;

  return (
    <div className="flex gap-2 border-b border-b-black-200 bg-white p-3">
      <div className="flex flex-1 items-center gap-2">
        {isLoading ? (
          <Skeleton className="size-10 rounded-full" />
        ) : (
          <ProfileImage src={selectedProfile.picture_url} />
        )}

        <div className="flex flex-col gap-0.5">
          {isLoading ? (
            <>
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-4 w-24" />
            </>
          ) : (
            <>
              <h5 className="line-clamp-1 break-all text-sm-bold">
                {selectedProfile.full_name}
              </h5>
              <p className="line-clamp-1 text-xs-medium text-black-500">
                {selectedProfile.occupation}
              </p>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="xs"
          intent="iconOnly"
          variant="secondary-black"
          onClick={() => setCurrentTargetIndex(currentTargetIndex - 1)}
          disabled={isLoading || currentTargetIndex === 0}
        >
          <CaretLeft />
        </Button>

        {isLoading ? (
          <Skeleton className="h-6 w-5" />
        ) : (
          <p className="text-nowrap text-button-sm">
            {currentTargetIndex + 1}
            <span className="text-black-400">/{targetProfiles.length}</span>
          </p>
        )}

        <Button
          size="xs"
          intent="iconOnly"
          variant="secondary-black"
          onClick={() => setCurrentTargetIndex(currentTargetIndex + 1)}
          disabled={
            isLoading || currentTargetIndex === targetProfiles.length - 1
          }
        >
          <CaretRight />
        </Button>
      </div>
    </div>
  );
}
